/**
 * AdminRoomDetail — Dettaglio sala (Fase 2).
 * Route: /admin/rooms/:room_id
 *
 * 2 TAB: "Mappa" (placeholder Fase 3) + "Elementi/Tavoli" (default).
 * La UI di gestione tavoli è integrata nella tab Elementi.
 * Touch-first: drag handle 44×44, bulk dialog con inputMode numeric.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, GripVertical, Archive, RotateCcw, Check, X,
  Map as MapIcon, LayoutGrid, Users, Palette, StickyNote, Ruler,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


const SHAPES = [
  { v: "square",    label: "Quadrato" },
  { v: "rectangle", label: "Rettangolo" },
  { v: "round",     label: "Rotondo" },
  { v: "oval",      label: "Ovale" },
  { v: "other",     label: "Altro" },
];


function SortableItemRow({ item, onOpenEdit, onArchive, onRestore }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: item.status === "archived" });
  const style = { transform: CSS.Transform.toString(transform), transition,
                   opacity: isDragging ? 0.55 : 1 };
  const archived = item.status === "archived";
  const stop = (e) => e.stopPropagation();

  return (
    <div ref={setNodeRef} style={style}
         data-testid={`item-row-${item.id}`}
         onClick={() => !archived && onOpenEdit(item)}
         className={`flex items-center gap-2 sm:gap-3 rounded-2xl border-2 px-2 sm:px-3 py-2 sm:py-2.5 bg-white
                     ${archived ? "opacity-60 border-sand-300 cursor-default"
                                : "border-sand-300 hover:border-bronze-500 cursor-pointer"}
                     transition-colors`}>
      {!archived && (
        <button type="button"
                data-testid={`item-drag-${item.id}`}
                {...attributes} {...listeners} onClick={stop}
                className="shrink-0 grid place-items-center h-11 w-11 rounded-lg text-ink-500
                           hover:bg-sand-100 active:bg-sand-200 cursor-grab touch-none select-none"
                aria-label={`Trascina ${item.name}`}>
          <GripVertical className="w-5 h-5" strokeWidth={1.75} />
        </button>
      )}
      {item.color && (
        <div className="shrink-0 h-5 w-5 rounded-md border border-sand-300"
             style={{ backgroundColor: item.color }} title={item.color} />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-sans font-medium text-ink-900 truncate">{item.name}</div>
        <div className="text-xs text-ink-500 mt-0.5 font-sans tabular-nums flex flex-wrap gap-x-3">
          <span>{SHAPES.find((s) => s.v === item.shape)?.label || item.shape}</span>
          {(item.capacity_min != null || item.capacity_max != null) && (
            <span>{item.capacity_min ?? "?"}–{item.capacity_max ?? "?"} coperti</span>
          )}
          {item.default_capacity != null && <span>def {item.default_capacity}</span>}
          {archived && <span className="text-red-700">archiviato</span>}
          {!archived && !item.is_active && <span className="text-amber-700">disattivato</span>}
        </div>
      </div>
      {!archived ? (
        <Button variant="ghost" size="icon"
                className="h-11 w-11 shrink-0 text-red-600 hover:bg-red-50"
                onClick={(e) => { stop(e); onArchive(item); }}
                data-testid={`item-archive-${item.id}`}>
          <Archive className="w-5 h-5" />
        </Button>
      ) : (
        <Button variant="ghost" size="sm"
                className="h-11 shrink-0 text-bronze-700"
                onClick={(e) => { stop(e); onRestore(item); }}
                data-testid={`item-restore-${item.id}`}>
          <RotateCcw className="w-4 h-4 mr-1" /> Ripristina
        </Button>
      )}
    </div>
  );
}


function BulkCreateDialog({ open, onOpenChange, room, onCreated }) {
  const [start, setStart] = useState(1);
  const [end, setEnd] = useState(10);
  const [prefix, setPrefix] = useState("T");
  const [capMin, setCapMin] = useState(2);
  const [capMax, setCapMax] = useState(4);
  const [defCap, setDefCap] = useState(4);
  const [shape, setShape] = useState("square");
  const [color, setColor] = useState("");
  const [busy, setBusy] = useState(false);

  const preview = useMemo(() => {
    const s = parseInt(start) || 0;
    const e = parseInt(end) || 0;
    const n = e - s + 1;
    if (n <= 0 || n > 200) return { names: [], count: n, tooMany: n > 200 };
    const p = (prefix || "").trim();
    const names = [];
    for (let i = s; i <= e; i++) names.push(p ? `${p}${i}` : String(i));
    return { names, count: n, tooMany: false };
  }, [start, end, prefix]);

  const submit = async () => {
    if (preview.tooMany) return toast.error("Massimo 200 elementi");
    setBusy(true);
    try {
      const body = {
        type: "table",
        start: parseInt(start), end: parseInt(end),
        prefix: (prefix || "").trim(),
        capacity_min: parseInt(capMin) || null,
        capacity_max: parseInt(capMax) || null,
        default_capacity: parseInt(defCap) || null,
        shape,
        color: color.trim() || null,
      };
      const r = await api.post(`/admin/rooms/${room.id}/items/bulk`, body);
      toast.success(`${r.data.created} tavoli creati in ${room.name}`);
      onCreated();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-dialog">
        <DialogHeader><DialogTitle>Aggiungi tavoli in {room?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Da</Label>
              <Input type="number" inputMode="numeric" value={start}
                     onChange={(e) => setStart(e.target.value)}
                     className="h-11" data-testid="bulk-start" />
            </div>
            <div>
              <Label className="text-xs">A</Label>
              <Input type="number" inputMode="numeric" value={end}
                     onChange={(e) => setEnd(e.target.value)}
                     className="h-11" data-testid="bulk-end" />
            </div>
            <div>
              <Label className="text-xs">Prefisso</Label>
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value)}
                     placeholder="T" maxLength={8} className="h-11 font-sans"
                     data-testid="bulk-prefix" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Cap. min</Label>
              <Input type="number" inputMode="numeric" value={capMin}
                     onChange={(e) => setCapMin(e.target.value)}
                     className="h-11 tabular-nums" data-testid="bulk-capmin" />
            </div>
            <div>
              <Label className="text-xs">Cap. max</Label>
              <Input type="number" inputMode="numeric" value={capMax}
                     onChange={(e) => setCapMax(e.target.value)}
                     className="h-11 tabular-nums" data-testid="bulk-capmax" />
            </div>
            <div>
              <Label className="text-xs">Cap. default</Label>
              <Input type="number" inputMode="numeric" value={defCap}
                     onChange={(e) => setDefCap(e.target.value)}
                     className="h-11 tabular-nums" data-testid="bulk-capdef" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Forma</Label>
              <select value={shape} onChange={(e) => setShape(e.target.value)}
                       className="w-full h-11 rounded-md border border-sand-300 bg-white px-3 font-sans"
                       data-testid="bulk-shape">
                {SHAPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Colore (opz.)</Label>
              <Input value={color} onChange={(e) => setColor(e.target.value)}
                     placeholder="#8B5A2B o nome CSS"
                     className="h-11 font-mono" data-testid="bulk-color" />
            </div>
          </div>
          <div className={`rounded-lg p-3 text-xs font-sans tabular-nums
                            ${preview.tooMany ? "bg-red-50 text-red-800" : "bg-sand-50 text-ink-700"}`}
               data-testid="bulk-preview">
            {preview.tooMany
              ? `⚠️ ${preview.count} elementi — massimo 200 per singola creazione.`
              : preview.count > 0
                ? <>Creerà <b>{preview.count}</b> tavoli: {preview.names.slice(0, 5).join(", ")}
                    {preview.count > 5 && ` … ${preview.names[preview.count - 1]}`}</>
                : "Intervallo non valido."}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy || preview.tooMany || preview.count <= 0}
                  data-testid="bulk-submit">
            <Plus className="w-4 h-4 mr-1" /> Crea {preview.count > 0 ? preview.count : ""} tavoli
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditItemDialog({ item, onClose, onSaved }) {
  const [name, setName] = useState(item.name);
  const [capMin, setCapMin] = useState(item.capacity_min ?? "");
  const [capMax, setCapMax] = useState(item.capacity_max ?? "");
  const [defCap, setDefCap] = useState(item.default_capacity ?? "");
  const [shape, setShape] = useState(item.shape || "square");
  const [color, setColor] = useState(item.color || "");
  const [notes, setNotes] = useState(item.notes || "");
  const [isActive, setIsActive] = useState(!!item.is_active);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        name: name.trim(),
        capacity_min: capMin === "" ? null : parseInt(capMin),
        capacity_max: capMax === "" ? null : parseInt(capMax),
        default_capacity: defCap === "" ? null : parseInt(defCap),
        shape,
        color: color.trim() || null,
        notes: notes.trim() || null,
        is_active: isActive,
      };
      await api.patch(`/admin/layout-items/${item.id}`, patch);
      toast.success(`${name} salvato`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-item-dialog">
        <DialogHeader><DialogTitle>Modifica tavolo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="h-11 font-sans" maxLength={60}
                   data-testid="edit-name" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Cap. min</Label>
              <Input type="number" inputMode="numeric" value={capMin}
                     onChange={(e) => setCapMin(e.target.value)}
                     className="h-11 tabular-nums" data-testid="edit-capmin" />
            </div>
            <div>
              <Label className="text-xs">Cap. max</Label>
              <Input type="number" inputMode="numeric" value={capMax}
                     onChange={(e) => setCapMax(e.target.value)}
                     className="h-11 tabular-nums" data-testid="edit-capmax" />
            </div>
            <div>
              <Label className="text-xs">Default</Label>
              <Input type="number" inputMode="numeric" value={defCap}
                     onChange={(e) => setDefCap(e.target.value)}
                     className="h-11 tabular-nums" data-testid="edit-capdef" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Forma</Label>
              <select value={shape} onChange={(e) => setShape(e.target.value)}
                       className="w-full h-11 rounded-md border border-sand-300 bg-white px-3 font-sans"
                       data-testid="edit-shape">
                {SHAPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Colore</Label>
              <Input value={color} onChange={(e) => setColor(e.target.value)}
                     placeholder="opz." className="h-11 font-mono"
                     data-testid="edit-color" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="font-sans" rows={2} maxLength={500}
                      data-testid="edit-notes" />
          </div>
          <label className="flex items-center gap-2 font-sans text-sm cursor-pointer">
            <input type="checkbox" checked={isActive}
                   onChange={(e) => setIsActive(e.target.checked)}
                   data-testid="edit-active" className="h-4 w-4" />
            Attivo
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !name.trim()}
                  data-testid="edit-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminRoomDetail() {
  const { room_id } = useParams();
  const [room, setRoom] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("items"); // 'map' | 'items'
  const [showBulk, setShowBulk] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const load = async () => {
    setLoading(true);
    try {
      const [rr, ri] = await Promise.all([
        api.get(`/admin/rooms/${room_id}`),
        api.get(`/admin/rooms/${room_id}/items?include_archived=${includeArchived}`),
      ]);
      setRoom(rr.data);
      setItems(ri.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [room_id, includeArchived]);

  const active = useMemo(() => items.filter((x) => x.status !== "archived"), [items]);
  const archived = useMemo(() => items.filter((x) => x.status === "archived"), [items]);

  const onDragEnd = async (event) => {
    const { active: a, over } = event;
    if (!over || a.id === over.id) return;
    const oldIdx = active.findIndex((r) => r.id === a.id);
    const newIdx = active.findIndex((r) => r.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(active, oldIdx, newIdx);
    const payload = reordered.map((r, i) => ({ id: r.id, list_order: (i + 1) * 10 }));
    setItems((prev) => {
      const map = new Map(payload.map((x) => [x.id, x.list_order]));
      return prev.map((r) => (map.has(r.id) ? { ...r, list_order: map.get(r.id) } : r))
                  .sort((x, y) => x.list_order - y.list_order);
    });
    try { await api.patch("/admin/layout-items/reorder", { items: payload });
           toast.success("Ordine aggiornato"); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const onArchive = async (item) => {
    if (!window.confirm(`Archiviare ${item.name}?`)) return;
    try { await api.post(`/admin/layout-items/${item.id}/archive`);
           toast.success(`${item.name} archiviato`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onRestore = async (item) => {
    try { await api.post(`/admin/layout-items/${item.id}/restore`);
           toast.success(`${item.name} ripristinato`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading && !room) return <div className="p-6 text-ink-500 font-sans">Caricamento…</div>;
  if (!room) return <div className="p-6 text-ink-500 font-sans">Sala non trovata.</div>;

  return (
    <div className="max-w-4xl" data-testid="admin-room-detail">
      <div className="mb-4">
        <Link to="/admin/rooms"
              className="text-sm text-ink-500 inline-flex items-center gap-1 hover:text-bronze-700 font-sans"
              data-testid="room-detail-back">
          <ChevronLeft className="w-4 h-4" /> Sale
        </Link>
        <div className="eyebrow mt-2">Configurazione sala</div>
        <div className="flex items-baseline flex-wrap gap-3 mt-1">
          <h1 className="text-3xl sm:text-4xl">{room.name}</h1>
          <span className="font-sans text-sm text-ink-500 tabular-nums">
            slug: <span className="font-mono">{room.slug}</span>
            {" · "}{room.tables_count ?? 0} {(room.tables_count ?? 0) === 1 ? "tavolo" : "tavoli"}
            {" · "}
            {room.status === "archived" ? "Archiviata" :
             room.is_active ? "Attiva" : "Disattivata"}
          </span>
        </div>
      </div>

      {/* TAB */}
      <div className="flex gap-2 border-b border-sand-300 mb-6"
           data-testid="room-detail-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "items"}
                onClick={() => setTab("items")}
                data-testid="room-detail-tab-items"
                className={`min-h-[44px] px-4 sm:px-5 font-sans font-medium text-sm sm:text-base
                            border-b-2 -mb-px transition-colors
                            ${tab === "items"
                              ? "border-bronze-600 text-bronze-700"
                              : "border-transparent text-ink-500 hover:text-ink-900"}`}>
          <LayoutGrid className="w-4 h-4 inline mr-1.5" /> Elementi / Tavoli
        </button>
        <button role="tab" aria-selected={tab === "map"}
                onClick={() => setTab("map")}
                data-testid="room-detail-tab-map"
                className={`min-h-[44px] px-4 sm:px-5 font-sans font-medium text-sm sm:text-base
                            border-b-2 -mb-px transition-colors
                            ${tab === "map"
                              ? "border-bronze-600 text-bronze-700"
                              : "border-transparent text-ink-500 hover:text-ink-900"}`}>
          <MapIcon className="w-4 h-4 inline mr-1.5" /> Mappa
        </button>
      </div>

      {/* Tab content · Elementi */}
      {tab === "items" && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Button onClick={() => setShowBulk(true)}
                    className="h-11 rounded-full"
                    data-testid="add-tables-btn">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi tavoli
            </Button>
            <label className="flex items-center gap-2 text-sm font-sans cursor-pointer ml-auto">
              <input type="checkbox" checked={includeArchived}
                     onChange={(e) => setIncludeArchived(e.target.checked)}
                     data-testid="items-toggle-archived" className="h-4 w-4" />
              Mostra archiviati ({archived.length})
            </label>
          </div>

          {loading ? (
            <div className="text-ink-400 py-6 font-sans">Caricamento…</div>
          ) : active.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-sand-300 p-10 text-center text-ink-500 font-sans"
                 data-testid="items-empty">
              <LayoutGrid className="w-8 h-8 mx-auto text-ink-400 mb-2" strokeWidth={1.5} />
              0 tavoli configurati in <span className="font-medium text-ink-700">{room.name}</span>.
              <div className="mt-3">
                <Button onClick={() => setShowBulk(true)} variant="outline"
                        className="rounded-full h-11">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi tavoli
                </Button>
              </div>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                         onDragEnd={onDragEnd}>
              <SortableContext items={active.map((r) => r.id)}
                                strategy={verticalListSortingStrategy}>
                <div className="space-y-2" data-testid="items-list">
                  {active.map((it) => (
                    <SortableItemRow key={it.id} item={it}
                                      onOpenEdit={setEditItem}
                                      onArchive={onArchive}
                                      onRestore={onRestore} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {includeArchived && archived.length > 0 && (
            <section className="mt-8" data-testid="items-archived-section">
              <h2 className="text-lg font-sans font-semibold text-ink-500 mb-3">
                Archiviati ({archived.length})
              </h2>
              <div className="space-y-2">
                {archived.map((it) => (
                  <SortableItemRow key={it.id} item={it}
                                    onOpenEdit={setEditItem}
                                    onArchive={onArchive}
                                    onRestore={onRestore} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Tab content · Mappa (placeholder Fase 3) */}
      {tab === "map" && (
        <div className="rounded-2xl border-2 border-dashed border-sand-300 p-10 sm:p-16 text-center text-ink-500 font-sans"
             data-testid="room-detail-map-placeholder">
          <MapIcon className="w-10 h-10 mx-auto text-ink-400 mb-3" strokeWidth={1.5} />
          <div className="font-medium text-ink-700 mb-1">Mappa {room.name}</div>
          <div className="text-sm max-w-md mx-auto">
            La disposizione visuale dei tavoli con drag &amp; drop touch sarà disponibile
            in una prossima release. Configura intanto l'anagrafica nella scheda Elementi.
          </div>
        </div>
      )}

      {showBulk && (
        <BulkCreateDialog open={showBulk}
                           onOpenChange={setShowBulk}
                           room={room}
                           onCreated={load} />
      )}
      {editItem && (
        <EditItemDialog item={editItem}
                         onClose={() => setEditItem(null)}
                         onSaved={load} />
      )}
    </div>
  );
}
